import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class CryptoService {
  private algRSAOAEP = {
    name: 'RSA-OAEP',
    modulusLength: 4096,
    publicExponent: new Uint8Array([1, 0, 1]),
    hash: 'SHA-256'
  };

  private cryp = window.crypto.subtle;
  private enc = new TextEncoder();
  private dec = new TextDecoder();

  constructor() { }

  async getKeyPairRSAOAEP(): Promise<{ pub: JsonWebKey, priv: JsonWebKey }> {
    const kk: CryptoKeyPair = await this.cryp.generateKey(this.algRSAOAEP, true, ['encrypt', 'decrypt']) as CryptoKeyPair;
    const keys = { pub: null, priv: null };
    keys.pub = await this.cryp.exportKey('jwk', kk.publicKey);
    keys.priv = await this.cryp.exportKey('jwk', kk.privateKey);
    return keys;
  }

  async encryptRSAOAEP(data: any, publicKey: JsonWebKey): Promise<string> {
    const pk = await this.cryp.importKey('jwk', publicKey, this.algRSAOAEP, true, ['encrypt']);
    const encoded = await this.cryp.encrypt(this.algRSAOAEP, pk, this.enc.encode(JSON.stringify(data)));
    const resp = String.fromCharCode(...new Uint8Array(encoded));
    // https://stackoverflow.com/a/50198295
    return resp;
  }

  async decryptRSAOAEP(data: string, privateKey: JsonWebKey): Promise<any> {
    const d = Uint8Array.from([...data].map(ch => ch.charCodeAt(0))).buffer;
    // https://stackoverflow.com/a/50198295
    const pk = await this.cryp.importKey('jwk', privateKey, this.algRSAOAEP, true, ['decrypt']);
    const decrypted = await this.cryp.decrypt(this.algRSAOAEP, pk, d);
    const decoded = await this.dec.decode(decrypted);
    return JSON.parse(decoded);
  }


  async getKeyIvAESGCM() {
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    const key = await this.cryp.generateKey({ name: "AES-GCM", length: 256 }, true, ['encrypt', 'decrypt']);
    return { key, iv }
  }

  async exportKeyIv(keys) {
    const rawKey = await this.cryp.exportKey('raw', keys.key);
    const keyString = String.fromCharCode(...new Uint8Array(rawKey));
    const ivString = String.fromCharCode(...new Uint8Array(keys.iv));
    return { key: keyString, iv: ivString }
  }

  async importKeyIv(keyStrings) {
    const key = await this.cryp.importKey('raw',
      Uint8Array.from([...keyStrings.key].map(ch => ch.charCodeAt(0))).buffer,
      { name: "AES-GCM", length: 256 },
      true,
      ['encrypt', 'decrypt']);
    const iv = Uint8Array.from([...keyStrings.iv].map(ch => ch.charCodeAt(0)));
    return { key, iv }
  }

  async encryptAESGCM(data, keys) {
    const dd = this.enc.encode(JSON.stringify(data));
    let enc = await this.cryp.encrypt({ name: "AES-GCM", iv: keys.iv }, keys.key, dd)
    let encTxt = String.fromCharCode(...new Uint8Array(enc));
    return encTxt
  }

  async decryptAESGCM(cipher, keys) {
    let decTxt = Uint8Array.from([...cipher].map(ch => ch.charCodeAt(0))).buffer;
    let decrypted = await this.cryp.decrypt({ name: "AES-GCM", iv:keys.iv }, keys.key, decTxt);
    let decoded = this.dec.decode(decrypted)
    return JSON.parse(decoded)
  }

}
